import React, { useState, useRef, useEffect } from 'react';
import { EnhancedImage } from './EnhancedImage';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X, ZoomIn, ZoomOut, RotateCw, Download, ChevronLeft, ChevronRight, Maximize2, Grid3X3, Images, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { AutoImageGenerator } from './AutoImageGenerator';
import { ProjectData } from '@/types/project';

// Import default images
import defaultResidentialImage from "@/assets/default-residential-property.webp";
import defaultCommercialImage from "@/assets/default-commercial-property.webp";

interface EnhancedPropertyImageGalleryProps {
  images: string[];
  floorPlans?: string[];
  propertyTitle?: string;
  propertyType?: string;
  reraNumber?: string;
  className?: string;
  property?: ProjectData;
  onImagesUpdated?: (newImages: string[]) => void;
}

interface ImageViewerState {
  scale: number;
  rotation: number;
  position: { x: number; y: number };
}

const EnhancedPropertyImageGallery = ({ 
  images, 
  floorPlans = [], 
  propertyTitle = "Property",
  propertyType = 'residential',
  reraNumber,
  className,
  property,
  onImagesUpdated
}: EnhancedPropertyImageGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState<'images' | 'floorplans'>('images');
  const [viewerState, setViewerState] = useState<ImageViewerState>({
    scale: 1,
    rotation: 0,
    position: { x: 0, y: 0 }
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayImages, setDisplayImages] = useState(images);

  // Update display images when images prop changes
  useEffect(() => {
    setDisplayImages(images);
  }, [images]);

  // Handle images update from auto generator
  const handleImagesUpdated = (newImages: string[]) => {
    setDisplayImages(newImages);
    onImagesUpdated?.(newImages);
  };

  const getDefaultImage = (propertyType: string) => {
    const isCommercial = propertyType.toLowerCase().includes('commercial') || 
                        propertyType.toLowerCase().includes('office') || 
                        propertyType.toLowerCase().includes('retail') ||
                        propertyType.toLowerCase().includes('warehouse') ||
                        propertyType.toLowerCase().includes('industrial');
    
    return isCommercial ? defaultCommercialImage : defaultResidentialImage;
  };

  const finalDisplayImages = (!displayImages || displayImages.length === 0) ? [getDefaultImage(propertyType)] : displayImages;
  const fallbackImage = getDefaultImage(propertyType);
  const allImages = currentTab === 'images' ? finalDisplayImages : floorPlans;
  const hasFloorPlans = floorPlans.length > 0;

  // Reset viewer state when switching tabs or images
  useEffect(() => {
    setViewerState({
      scale: 1,
      rotation: 0,
      position: { x: 0, y: 0 }
    });
  }, [selectedIndex, currentTab]);

  const openLightbox = (index: number, tab: 'images' | 'floorplans' = 'images') => {
    setCurrentTab(tab);
    setSelectedIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setViewerState({
      scale: 1,
      rotation: 0,
      position: { x: 0, y: 0 }
    });
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    const maxIndex = allImages.length - 1;
    if (direction === 'prev') {
      setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : maxIndex);
    } else {
      setSelectedIndex(selectedIndex < maxIndex ? selectedIndex + 1 : 0);
    }
  };

  const handleZoom = (factor: number) => {
    setViewerState(prev => ({
      ...prev,
      scale: Math.max(0.5, Math.min(5, prev.scale * factor))
    }));
  };

  const handleRotate = () => {
    setViewerState(prev => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360
    }));
  };

  const handleReset = () => {
    setViewerState({
      scale: 1,
      rotation: 0,
      position: { x: 0, y: 0 }
    });
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(allImages[selectedIndex]);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${propertyTitle}-${currentTab}-${selectedIndex + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (viewerState.scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - viewerState.position.x,
        y: e.clientY - viewerState.position.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && viewerState.scale > 1) {
      setViewerState(prev => ({
        ...prev,
        position: {
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        }
      }));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isLightboxOpen) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        navigateImage('prev');
        break;
      case 'ArrowRight':
        navigateImage('next');
        break;
      case 'Escape':
        closeLightbox();
        break;
      case '+':
      case '=':
        handleZoom(1.2);
        break;
      case '-':
        handleZoom(0.8);
        break;
      case 'r':
      case 'R':
        handleRotate();
        break;
      case '0':
        handleReset();
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, selectedIndex, currentTab]);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Auto Image Generator */}
      {property && (
        <AutoImageGenerator
          property={property}
          images={displayImages}
          onImagesUpdated={handleImagesUpdated}
        />
      )}
      
      {/* Main Image Display */}
      <div className="relative group">
          <Carousel
            opts={{ align: "start", loop: true }}
            plugins={[Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })]}
            className="w-full"
          >
            <CarouselContent>
              {finalDisplayImages.map((src, i) => (
                <CarouselItem key={i}>
                  <div className="aspect-video w-full overflow-hidden rounded-lg bg-muted">
                    <EnhancedImage
                      src={src}
                      fallbackSrc={fallbackImage}
                      optimizedWidth={1200}
                      alt={`${propertyTitle} - Image ${i + 1}`}
                      className="w-full h-full object-cover cursor-pointer transition-transform hover:scale-105"
                      onClick={() => openLightbox(i, 'images')}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      enableEnhancement={true}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="bg-white/90 hover:bg-white shadow z-10 -left-3 md:-left-5" />
            <CarouselNext className="bg-white/90 hover:bg-white shadow z-10 -right-3 md:-right-5" />
          </Carousel>
        
        {/* Image Count Badge (top-right) */}
        <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
          <Images className="h-4 w-4 inline mr-1" />
          {finalDisplayImages.length} {finalDisplayImages.length === 1 ? 'Photo' : 'Photos'}
        </div>

        {/* RERA Badge (top-left) */}
        {reraNumber && (
          <div className="absolute top-3 left-3">
            <Badge variant="secondary">RERA: {reraNumber}</Badge>
          </div>
        )}

        {/* View Gallery Button */}
        <Button
          variant="secondary"
          size="sm"
          className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => openLightbox(0, 'images')}
        >
          <Maximize2 className="h-4 w-4 mr-2" />
          View Gallery
        </Button>
      </div>

      {/* Thumbnail Grid */}
      <div className="space-y-3">
        {/* Images Section */}
        {finalDisplayImages.length > 1 && (
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center">
              <Images className="h-4 w-4 mr-2" />
              Property Images
            </h4>
            <div className="grid grid-cols-5 gap-2">
              {finalDisplayImages.slice(1, 6).map((image, index) => (
                <div
                  key={index}
                  className="aspect-square overflow-hidden rounded-md cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => openLightbox(index + 1, 'images')}
                >
                  <EnhancedImage
                    src={image}
                    fallbackSrc={fallbackImage}
                    optimizedWidth={240}
                    alt={`${propertyTitle} - Image ${index + 2}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              
              {/* Show more button if there are more than 5 images */}
              {finalDisplayImages.length > 6 && (
                <div
                  className="aspect-square bg-muted rounded-md cursor-pointer hover:bg-muted/80 transition-colors flex flex-col items-center justify-center"
                  onClick={() => openLightbox(6, 'images')}
                >
                  <Grid3X3 className="h-6 w-6 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground mt-1">
                    +{finalDisplayImages.length - 6}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Floor Plans Section */}
        {hasFloorPlans && (
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center">
              <FileText className="h-4 w-4 mr-2" />
              Floor Plans
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {floorPlans.map((plan, index) => (
                <Card
                  key={index}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openLightbox(index, 'floorplans')}
                >
                  <CardContent className="p-2">
                    <div className="aspect-square overflow-hidden rounded-md mb-2">
                      <EnhancedImage
                        src={plan}
                        fallbackSrc={fallbackImage}
                        optimizedWidth={480}
                        alt={`Floor Plan ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <Badge variant="outline" className="text-xs">
                      Floor Plan {index + 1}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Lightbox */}
      {isLightboxOpen && (
        <Dialog open={isLightboxOpen} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] p-0 overflow-hidden">
          <div className="flex flex-col h-full min-h-0">
            {/* Header */}
            <DialogHeader className="p-4 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <DialogTitle className="text-lg">
                    {propertyTitle} - {currentTab === 'images' ? 'Images' : 'Floor Plans'}
                  </DialogTitle>
                  
                  {/* Tab Switcher */}
                  {hasFloorPlans && (
                    <div className="flex items-center space-x-2">
                      <Button
                        variant={currentTab === 'images' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setCurrentTab('images');
                          setSelectedIndex(0);
                        }}
                      >
                        <Images className="h-4 w-4 mr-2" />
                        Images ({finalDisplayImages.length})
                      </Button>
                      <Button
                        variant={currentTab === 'floorplans' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          setCurrentTab('floorplans');
                          setSelectedIndex(0);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Floor Plans ({floorPlans.length})
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Image Counter */}
                <Badge variant="outline">
                  {selectedIndex + 1} / {allImages.length}
                </Badge>
              </div>
            </DialogHeader>

            {/* Main Content */}
            <div className="flex-1 flex min-h-0">
              {/* Image Viewer */}
              <div 
                ref={containerRef}
                className="flex-1 min-w-0 min-h-0 relative bg-black/5 overflow-hidden flex items-center justify-center"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {allImages[selectedIndex] && (
                  <EnhancedImage
                    src={allImages[selectedIndex]}
                    fallbackSrc={fallbackImage}
                    optimizedWidth={1600}
                    alt={`${propertyTitle} - ${currentTab} ${selectedIndex + 1}`}
                    className="w-full h-full object-contain transition-transform"
                    style={{
                      transform: `scale(${viewerState.scale}) rotate(${viewerState.rotation}deg) translate(${viewerState.position.x}px, ${viewerState.position.y}px)`,
                      cursor: viewerState.scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                    }}
                    draggable={false}
                  />
                )}

                {/* Navigation Arrows */}
                {allImages.length > 1 && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white"
                      onClick={() => navigateImage('prev')}
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white"
                      onClick={() => navigateImage('next')}
                    >
                      <ChevronRight className="h-6 w-6" />
                    </Button>
                  </>
                )}
              </div>

              {/* Sidebar Controls */}
              <div className="w-20 border-l bg-background p-2 space-y-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleZoom(1.2)}
                  disabled={viewerState.scale >= 5}
                  title="Zoom In (+)"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleZoom(0.8)}
                  disabled={viewerState.scale <= 0.5}
                  title="Zoom Out (-)"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleRotate}
                  title="Rotate (R)"
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleReset}
                  title="Reset View (0)"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleDownload}
                  title="Download Image"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Thumbnail Strip */}
            {allImages.length > 1 && (
              <div className="border-t p-4">
                <div className="flex space-x-2 overflow-x-auto">
                  {allImages.map((image, index) => (
                    <div
                      key={index}
                      className={cn(
                        "w-16 h-16 flex-shrink-0 overflow-hidden rounded cursor-pointer border-2 transition-all",
                        selectedIndex === index 
                          ? "border-primary shadow-md" 
                          : "border-transparent hover:border-muted-foreground"
                      )}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <EnhancedImage
                        src={image}
                        fallbackSrc={fallbackImage}
                        optimizedWidth={160}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EnhancedPropertyImageGallery;